import { create } from 'zustand'
import { PresenceState, type UserPresenceState } from '@api/types/presence'

/**
 * Who the app currently cares about, and the last thing the server said about them.
 *
 * The watch list is reference-counted rather than a plain set: a chat header, a members dialog
 * and a conversation list can all want the same person, and the last of them to unmount must not
 * stop the others being updated. Counting is what keeps one heartbeat serving every component.
 *
 * Not persisted. Presence is worthless a moment later, and a stale dot restored from a previous
 * session would claim somebody is online who logged off yesterday.
 */
interface PresenceStoreState {
  /** Last known state per user id. */
  states: Record<string, UserPresenceState>
  /** userId → how many mounted components are watching them. */
  watchCounts: Record<string, number>

  watch: (userIds: string[]) => void
  unwatch: (userIds: string[]) => void
  /** The ids to send with the next heartbeat. */
  watchedIds: () => string[]
  apply: (users: UserPresenceState[]) => void
  clear: () => void
}

export const usePresenceStore = create<PresenceStoreState>((set, get) => ({
  states: {},
  watchCounts: {},

  watch: (userIds) =>
    set((s) => {
      const counts = { ...s.watchCounts }
      for (const id of userIds) counts[id] = (counts[id] ?? 0) + 1
      return { watchCounts: counts }
    }),

  unwatch: (userIds) =>
    set((s) => {
      const counts = { ...s.watchCounts }
      for (const id of userIds) {
        const next = (counts[id] ?? 0) - 1
        if (next > 0) counts[id] = next
        else delete counts[id]
      }
      return { watchCounts: counts }
    }),

  watchedIds: () => Object.keys(get().watchCounts),

  apply: (users) =>
    set((s) => {
      if (users.length === 0) return s
      const states = { ...s.states }
      for (const u of users) states[u.userId] = u
      return { states }
    }),

  /**
   * Forget what we know, but NOT who is being watched.
   *
   * `watchCounts` is reference-counted by mounted components — usePresence registers on mount and
   * releases on unmount. Clearing it here wiped registrations belonging to components that were
   * still on screen, and nothing re-registered them, because usePresence's effect is keyed on the
   * id list rather than on the session. The heartbeat then asked about nobody for the rest of the
   * session and no status ever changed until something remounted, which is why only a refresh
   * appeared to fix it. React runs child effects before parent effects, so a chat header could
   * register and be erased microseconds later by the layout above it.
   */
  clear: () => set({ states: {} }),
}))

/** Offline is the honest default for somebody we have not heard about. */
export const OFFLINE: UserPresenceState = {
  userId: '',
  state: PresenceState.Offline,
  lastSeenAt: null,
}
